import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import classNames from 'classnames';
import get from 'lodash.get';
import throttle from 'lodash.throttle';

import { getDisplayNameForVertical } from 'lib/vertical';
import { package as packagePropType } from 'lib/CustomPropTypes';
import TitleLogo from 'components/TitleLogo';
import Breakpoints from 'lib/Breakpoints';

import Link from 'components/Link';
import ModalDisclaimer from 'components/ModalDisclaimer/index';

import { Author } from './Cards/Author';
import OneByOneProduct from './Cards/OneByOneProduct';
import TwoByOne from './Cards/TwoByOne';
import TwoByThree from './Cards/TwoByThree';

// Temporarily leaving this stylesheet to handle grid only styles
// until different solution for grid is implemented within new theming strategy
import './styles.module.scss';

const BACON_CARD_WIDTHS = {
  default: 260,
  author: 263,
  oneOneProduct: 260,
  twoOneSmall: 260,
  twoOneLarge: () => (Breakpoints.isS() ? 260 : 360),
  twoThreePortrait: 250,
};

const BACON_BUTTON_WIDTH = 50;

const CARD_GUTTER = 2;

const CARD_MAP = {
  default: TwoByOne,
  author: Author,
  oneOneProduct: OneByOneProduct,
  twoOneSmall: TwoByOne,
  twoOneLarge: TwoByOne,
  twoThreePortrait: TwoByThree,
};

const disclaimerText = (vertical) => `${vertical} may make a share of the revenue from your purchase of these products.`;
const disclaimerDetailText = (vertical) => `${vertical} editors, writers and experts take care to recommend items we really like and hope you'll enjoy! Just so you know, ${vertical} does have affiliate relationships. So, while every product is independently selected, if you buy something through our links, we may get a small share of the revenue. We have not been paid to promote any of these products.`;

class Bacon extends React.Component {
  static propTypes = {
    content: packagePropType.isRequired,
    vertical: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.translateContainer = React.createRef();
    this.scrollContainer = React.createRef();

    this.state = {
      isScrolling: false,
      translateTo: 0,
      showPagination: false,
      currentPage: 1,
      totalPages: 1,
      scrollLeft: 0,
    };

    const { content: { subType } } = props;
    this.isProduct = subType && subType.toLowerCase().indexOf('product') > -1;
  }

  componentDidMount() {
    // Add a breakpoint change listener
    Breakpoints.listenToAll(this.onBreakpointChange);

    this.translateContainer.current.addEventListener('transitionend', this.onTransitionEnd);
    this.scrollContainer.current.addEventListener('scroll', this.onContainerScroll);

    const { currentPage, totalPages, hasMultiplePages } = this.getContainerMeasurements();
    if (!Breakpoints.isS() && hasMultiplePages) {
      this.setState({ showPagination: true, currentPage, totalPages });
    }
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot !== null) {
      const { current: scrollContainer } = this.scrollContainer;
      // minus in order to offset the negative translateX
      scrollContainer.scrollLeft -= snapshot;
    }
  }

  componentWillUnmount() {
    // Remove the breakpoint change listener
    Breakpoints.removeFromAll(this.onBreakpointChange);

    this.translateContainer.current.removeEventListener('transitionend', this.onTransitionEnd);
    this.scrollContainer.current.removeEventListener('scroll', this.onContainerScroll);
  }

  onContainerScroll = throttle(() => {
    const { currentPage, totalPages, scrollLeft } = this.state;
    const {
      currentPage: newCurrentPage,
      totalPages: newTotalPages,
      scrollLeft: newScrollLeft,
    } = this.getContainerMeasurements();
    if (
      newCurrentPage !== currentPage
      || newTotalPages !== totalPages
      || ((newScrollLeft === 0 && scrollLeft !== 0) || (newScrollLeft !== 0 && scrollLeft === 0))
    ) {
      this.setState({
        currentPage: newCurrentPage,
        totalPages: newTotalPages,
        scrollLeft: newScrollLeft,
      });
    }
  }, 200);

  getSnapshotBeforeUpdate(_, prevState) {
    const { translateTo: newTranslateTo } = this.state;
    if (prevState.translateTo && newTranslateTo === 0) {
      return prevState.translateTo;
    }
    return null;
  }

  onBreakpointChange = () => {
    if (Breakpoints.isS()) {
      this.setState({ showPagination: false });
    } else {
      const { currentPage, totalPages } = this.getContainerMeasurements();
      this.setState({ showPagination: totalPages > 1, currentPage, totalPages });
    }
  }

  onTransitionEnd = (e) => {
    if (e.propertyName === 'transform' && e.target === this.translateContainer.current) {
      const { isScrolling } = this.state;
      if (isScrolling) {
        this.setState({ isScrolling: false, translateTo: 0 });
      }
    }
  }

  getSlideWidth = () => {
    const { content } = this.props;
    const cardType = content && (content.subType || content.cardType);

    if (typeof BACON_CARD_WIDTHS[cardType] === 'function') {
      return BACON_CARD_WIDTHS[cardType]();
    }

    return BACON_CARD_WIDTHS[cardType] || BACON_CARD_WIDTHS.default;
  }

  getNumCards = () => get(this.props, 'content.items', []).filter((c) => c).length

  getContainerMeasurements = () => {
    const { showPagination } = this.state;
    const { current: scrollContainer } = this.scrollContainer;

    if (!scrollContainer) return {};

    const numCards = this.getNumCards();
    const slideWidth = this.getSlideWidth();

    const containerWidth = scrollContainer.clientWidth;
    const { scrollWidth, scrollLeft } = scrollContainer;
    const firstPageFrameWidth = showPagination
      ? containerWidth
      // account for width of next button if not already rendered
      : containerWidth - BACON_BUTTON_WIDTH;
    const nthPageFrameWidth = showPagination
      ? containerWidth - BACON_BUTTON_WIDTH
      // account for width of next & prev buttons if not already rendered
      : containerWidth - (BACON_BUTTON_WIDTH * 2);
    const numCardsFirstPage = Math.floor(firstPageFrameWidth / (slideWidth + CARD_GUTTER));
    const numCardsNthPage = Math.floor(nthPageFrameWidth / (slideWidth + CARD_GUTTER));
    const hasMultiplePages = numCards > numCardsFirstPage;
    const widthOfFirstPage = (numCardsFirstPage * (slideWidth + CARD_GUTTER));
    const widthOfNthPage = (numCardsNthPage * (slideWidth + CARD_GUTTER));
    const amountScrolledPastFirstPage = scrollLeft - (widthOfFirstPage - BACON_BUTTON_WIDTH);

    const totalPages = hasMultiplePages
      ? Math.ceil(
        // figure out how many "nth" pages there are, including the last sliver of a card
        (scrollWidth - widthOfFirstPage) / widthOfNthPage,
      ) + 1 // add one for the first page
      : 1;

    let currentPage = 1;
    if (scrollLeft + containerWidth === scrollWidth) {
      currentPage = totalPages;
    } else if (scrollLeft >= (widthOfFirstPage - BACON_BUTTON_WIDTH)) {
      // add 2, one for the first page, one for making it 1-based instead of 0-based
      currentPage = Math.floor(amountScrolledPastFirstPage / widthOfNthPage) + 2;
    }

    return {
      amountScrolledPastFirstPage,
      containerWidth,
      currentPage,
      firstPageFrameWidth,
      hasMultiplePages,
      nthPageFrameWidth,
      numCards,
      numCardsFirstPage,
      numCardsNthPage,
      scrollLeft,
      scrollWidth,
      slideWidth,
      totalPages,
      widthOfFirstPage,
      widthOfNthPage,
      getTransAmtFromPageNum: (page) => {
        if (page === 1) {
          return scrollLeft;
        }
        if (page === totalPages) {
          return scrollLeft - (scrollWidth - containerWidth);
        }
        if (page === 2) {
          return amountScrolledPastFirstPage;
        }
        // subtract 2, one for the firstPage, one for how many pixels past the 2nd page you
        // want to scroll to
        return amountScrolledPastFirstPage - ((page - 2) * widthOfNthPage);
      },
    };
  }

  scrollToNextPage = () => {
    const { isScrolling } = this.state;
    const { current: scrollContainer } = this.scrollContainer;

    if (!scrollContainer || isScrolling) return;

    const {
      containerWidth,
      hasMultiplePages,
      scrollLeft,
      scrollWidth,
      widthOfFirstPage,
      widthOfNthPage,
      currentPage,
      totalPages,
    } = this.getContainerMeasurements();

    if (!hasMultiplePages) return;

    let translateAmount = 0;

    if (scrollLeft + containerWidth === scrollWidth) {
      // currently on last page, scroll all the way back to start
      translateAmount = scrollLeft;
    } else if ((scrollWidth - scrollLeft) - containerWidth < widthOfNthPage) {
      // less than a whole page left, scroll only the remaining amount
      translateAmount = -((scrollWidth - scrollLeft) - containerWidth);
    } else if (scrollLeft < (widthOfFirstPage - BACON_BUTTON_WIDTH)) {
      // currently on first page, scrolling to 2nd page
      translateAmount = -((widthOfFirstPage - BACON_BUTTON_WIDTH) - scrollLeft);
    } else {
      const amountScrolledPastFirstPage = scrollLeft - (widthOfFirstPage - BACON_BUTTON_WIDTH);
      const relativeAmountOfNextPageShowing = amountScrolledPastFirstPage < widthOfNthPage
        ? amountScrolledPastFirstPage
        : amountScrolledPastFirstPage % widthOfNthPage;
      translateAmount = -(widthOfNthPage - relativeAmountOfNextPageShowing);
    }
    this.setState({
      translateTo: translateAmount,
      isScrolling: true,
      currentPage: currentPage === totalPages ? 1 : currentPage + 1,
      totalPages,
      scrollLeft: scrollLeft - translateAmount,
    });
  }

  scrollToPrevPage = () => {
    const { isScrolling } = this.state;
    const { current: scrollContainer } = this.scrollContainer;

    if (!scrollContainer || isScrolling) return;

    const {
      hasMultiplePages,
      scrollLeft,
      widthOfFirstPage,
      widthOfNthPage,
      currentPage,
      totalPages,
    } = this.getContainerMeasurements();

    if (!hasMultiplePages || scrollLeft === 0) return;

    let translateAmount = 0;

    if (scrollLeft <= (widthOfFirstPage - BACON_BUTTON_WIDTH)) {
      // currently on 2nd page, scroll back to the start
      translateAmount = scrollLeft;
    } else {
      const amountScrolledPastFirstPage = scrollLeft - (widthOfFirstPage - BACON_BUTTON_WIDTH);
      // if there's 0 left over, we're currently at the start of a page and
      // we need to scroll back one whole page
      translateAmount = amountScrolledPastFirstPage % widthOfNthPage || widthOfNthPage;
    }

    // if you're on the 2nd page and not scrolling all the way back to the start,
    // it's still the 2nd page
    const newCurrentPage = (currentPage === 2 && translateAmount !== scrollLeft)
      // or if you're not on the 2nd page, and not on the last page, and you're scrolling
      // less than the width of a whole page, you're still on the same page
      || (currentPage !== 2 && currentPage !== totalPages && translateAmount < widthOfNthPage)
      ? currentPage
      : (currentPage - 1) || 1;

    this.setState({
      translateTo: translateAmount,
      isScrolling: true,
      currentPage: newCurrentPage,
      totalPages,
      scrollLeft: scrollLeft - translateAmount,
    });
  }

  scrollToPage = (pageNum) => {
    const { getTransAmtFromPageNum, scrollLeft } = this.getContainerMeasurements();
    const translateAmount = getTransAmtFromPageNum(pageNum);
    this.setState({
      translateTo: translateAmount,
      isScrolling: true,
      currentPage: pageNum,
      scrollLeft: scrollLeft - translateAmount,
    });
  }

  renderTitleDesc = () => {
    const {
      title, logoUrl, description, seeAllText, seeAllUrl,
    } = get(this.props, 'content.metadata', {});
    const { vertical } = this.props;

    if (!(title || logoUrl) && !description) return null;
    return (
      <div className="bacon__header-headline">
        {(title || logoUrl)
          && (
          <h2 className="bacon__title" data-test="bacon__header__headline">
            <TitleLogo logoUrl={logoUrl} title={title} />
            {seeAllUrl
              && (
                <Link className="bacon__title__link" to={seeAllUrl} data-test="bacon__header__see-all-link">
                  {seeAllText || i18next.t('SEE ALL')}
                </Link>
              )}
          </h2>
          )}
        {description
          && (
          <p className="bacon__description" data-test="bacon__header__dek">
            {description}
          </p>
          )}
        {this.isProduct && this.renderDisclaimer(vertical)}
      </div>
    );
  }

  renderDisclaimer = (vertical) => {
    const verticalDisplayName = getDisplayNameForVertical(vertical);
    return (
      <ModalDisclaimer disclaimerDetailText={disclaimerDetailText(verticalDisplayName)}>
        <span className="bacon__disclaimer">
          {disclaimerText(verticalDisplayName)}
        </span>
      </ModalDisclaimer>
    );
  }

  renderPagingDots = () => {
    const { currentPage, totalPages, showPagination } = this.state;

    if (!showPagination) {
      return null;
    }

    return (
      <div className="bacon__paging-dots" data-test="bacon__header__paging-dots">
        {Array.from({ length: totalPages }).map((_, i) => {
          const dotPageNum = i + 1;
          const isCurrentPage = currentPage === dotPageNum;

          return (
            <button
              type="button"
              aria-label="Pagination"
              key={`${dotPageNum}/${totalPages}`}
              onClick={() => this.scrollToPage(dotPageNum)}
              className={classNames(
                'bacon__paging-dot',
                {
                  'bacon__paging-dot--is-current': isCurrentPage,
                },
              )}
            />
          );
        })}
      </div>
    );
  }

  renderPrevButton = () => {
    const { showPagination, scrollLeft } = this.state;
    const { content: { cardType } } = this.props;

    if (!scrollLeft && cardType === 'author') return null;

    return (
      <div
        className={classNames(
          'bacon__prev-btn__container',
          { 'bacon__prev-btn--active': showPagination && scrollLeft !== 0 },
        )}
        data-test="bacon__page-btns__prev-container"
      >
        <button
          type="button"
          className="bacon__page-btn--prev"
          onClick={this.scrollToPrevPage}
        >
          <span className="bacon__page-btn--icon icon icon-angle-down" />
        </button>
      </div>
    );
  }

  renderNextButton = () => {
    const { showPagination } = this.state;

    return (
      <div
        className={classNames(
          'bacon__next-btn__container',
          { 'bacon__next-btn__container--no-pagination': !showPagination },
        )}
        data-test="bacon__page-btns__next-container"
      >
        <button
          type="button"
          className="bacon__page-btn--next"
          onClick={this.scrollToNextPage}
        >
          <span className="bacon__page-btn--icon icon icon-angle-down" />
        </button>
      </div>
    );
  }

  renderCard = (card) => {
    if (!card || !get(card, 'computedValues')) return null;
    const {
      content: {
        type,
        subType,
        metadata = {},
        cardType = undefined,
      },
    } = this.props;
    const Card = CARD_MAP[subType] || CARD_MAP[cardType] || CARD_MAP.default;
    const author = get(card, 'item.authors[0]', undefined);
    const authorName = get(author, ['person', 'name'], undefined);
    const authorPhotoUrl = get(author, ['person', 'primaryImage', 'url', 'primary'], undefined);
    if (cardType === 'author' && !authorName && !authorPhotoUrl) return null;
    return (
      <div
        key={card.id}
        className="bacon__card-wrapper"
        style={{ width: `${this.getSlideWidth()}px` }}
      >
        <Card
          card={card}
          showEyebrow={metadata.showEyebrow}
          showTimestamp={metadata.showTimestamp}
          pkgType={type}
        />
      </div>
    );
  }

  render() {
    if (!get(this.props, 'content.items.length', false)) {
      return null;
    }
    const { content } = this.props;
    const { translateTo, isScrolling, showPagination } = this.state;

    return (
      <section
        className={classNames({ isShowingPagination: showPagination }, 'bacon__package pkg')}
        data-packageid={content.id}
        data-test="bacon__container"
      >
        <div className="bacon__header--top-border" data-test="bacon__header__top-border" />
        <div className="bacon__header">
          {this.renderTitleDesc()}
          {this.renderPagingDots()}
        </div>

        <div className="bacon__scroll__wrapper">
          {this.renderPrevButton()}
          <div className="bacon__scroll__container" ref={this.scrollContainer}>
            <div
              className={classNames(
                'bacon__scroll__translate-container',
                { 'bacon__scroll--is-scrolling': isScrolling },
              )}
              ref={this.translateContainer}
              style={{ transform: `translateX(${translateTo}px)` }}
            >
              {content.items.map(this.renderCard)}
            </div>
          </div>
          {this.renderNextButton()}
        </div>
      </section>
    );
  }
}

export default Bacon;
